import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, formatDateTime } from "../../utils/HelperFunctions";
import { ArrowLeftIcon, ArchiveBoxIcon, EyeIcon, MegaphoneIcon, GlobeAltIcon, BuildingOffice2Icon } from "@heroicons/react/24/outline";
import { ProgressSpinner } from "primereact/progressspinner";
import ImagePreviewModal from "../../components/common/ImagePreviewModal";

const ProductView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [record, setRecord] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [previewIndex, setPreviewIndex] = useState<number | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const response = await apiFunctions.get(`/product/admin/${id}`);
                if (response?.data?.success) {
                    setRecord(response.data.data);
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) {
        return (
            <div className="unlist-margin flex justify-center items-center" style={{ minHeight: "300px" }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="unlist-margin">
            <div className="back-button mb-2" onClick={() => navigate("/admin/products")}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>

            <Card>
                <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
                    <h2 className="m-0">View Product</h2>
                    <div className="flex gap-2 flex-wrap">
                        <Button
                            label="View Public Page"
                            icon={<GlobeAltIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                            outlined
                            severity="secondary"
                            onClick={() => window.open(`https://sagarsamaj.in/product/${id}`, "_blank")}
                        />
                        <Button
                            label={`Viewers (${record?.viewCount ?? 0})`}
                            icon={<EyeIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                            outlined
                            onClick={() => navigate(`/admin/products/viewers/${id}`)}
                        />
                        <Button
                            label="Create Advertisement"
                            icon={<MegaphoneIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                            severity="help"
                            outlined
                            onClick={() => navigate("/admin/advertisement/add", {
                                state: {
                                    prefill: {
                                        company_name: record?.businessName || record?.name || "",
                                        mobile_number: record?.ownerMobile || "",
                                        userid: record?.userid || "",
                                        ownerName: record?.ownerName || "",
                                        imageUrl: record?.imageUrls?.[0] || "",
                                        short_description: record?.description && record.description.length <= 30 ? record.description : "",
                                        long_description: record?.description ? record.description : "",
                                    }
                                }
                            })}
                        />
                        <Button label="Edit" onClick={() => navigate(`/admin/products/edit/${id}`)} />
                    </div>
                </div>

                {record && (
                    <div className="grid grid-cols-12 gap-4">

                        <div className="col-span-12 flex items-center gap-4">
                            {record.imageUrls?.length > 0 ? (
                                <img src={record.imageUrls[0]} alt={record.name} style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "10px", border: "1px solid #e5e7eb" }} />
                            ) : (
                                <div style={{ width: "80px", height: "80px", borderRadius: "10px", border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <ArchiveBoxIcon style={{ width: "36px", height: "36px", color: "#d1d5db" }} />
                                </div>
                            )}
                            <div>
                                <h3 className="m-0">{record.name}</h3>
                                <p className="m-0 text-gray-500">{record.nameGujarati || "-"}</p>
                                <Tag className="mt-1" value={record.isactive ? "Active" : "Inactive"} severity={record.isactive ? "success" : "danger"} />
                            </div>
                        </div>

                        <div className="lg:col-span-6 col-span-12"><strong>Price:</strong> {record.price != null ? `₹${record.price}` : "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Category:</strong> {record.categoryName || "-"}</div>
                        <div className="lg:col-span-6 col-span-12">
                            <strong>Business:</strong>{" "}
                            {record.business ? (
                                <span
                                    className="cursor-pointer"
                                    style={{ color: "#264164", fontWeight: 600 }}
                                    onClick={() => navigate(`/admin/business/view/${record.business.id}`)}
                                >
                                    {record.business.name} →
                                </span>
                            ) : (record.businessName || "-")}
                        </div>
                        <div className="lg:col-span-6 col-span-12"><strong>Owner:</strong> {record.ownerName || "-"}</div>
                        <div className="col-span-12"><strong>Description:</strong> {record.description || "-"}</div>
                        <div className="lg:col-span-6 col-span-12">
                            <strong>Created Date:</strong> {formatDateTime(record.createddate)}
                        </div>

                        {record.imageUrls?.length > 0 && (
                            <div className="col-span-12">
                                <strong>Images:</strong>
                                <div className="flex gap-3 flex-wrap mt-2">
                                    {record.imageUrls.map((url: string, i: number) => (
                                        <img
                                            key={i}
                                            src={url}
                                            alt={`product-${i}`}
                                            onClick={() => setPreviewIndex(i)}
                                            style={{ width: "110px", height: "110px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e5e7eb", cursor: "pointer" }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Similar products (same business, or same seller) */}
                        {record.similarProducts?.length > 0 && (
                            <div className="col-span-12">
                                <strong className="flex items-center gap-2">
                                    <BuildingOffice2Icon style={{ width: "18px", height: "18px" }} />
                                    Similar Products ({record.similarProducts.length})
                                </strong>
                                <div className="grid grid-cols-12 gap-3 mt-2">
                                    {record.similarProducts.map((p: any) => (
                                        <div
                                            key={p.id}
                                            className="col-span-6 sm:col-span-4 lg:col-span-3 cursor-pointer"
                                            style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}
                                            onClick={() => navigate(`/admin/products/view/${p.id}`)}
                                        >
                                            {p.imageUrl ? (
                                                <img src={p.imageUrl} alt={p.name} style={{ width: "100%", height: "100px", objectFit: "cover" }} />
                                            ) : (
                                                <div style={{ width: "100%", height: "100px", background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <ArchiveBoxIcon style={{ width: "28px", height: "28px", color: "#d1d5db" }} />
                                                </div>
                                            )}
                                            <div style={{ padding: "8px 10px" }}>
                                                <p className="m-0 text-sm font-medium" style={{ color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</p>
                                                <p className="m-0 text-xs" style={{ color: "#264164", fontWeight: 700 }}>₹{p.price}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                )}
            </Card>

            {previewIndex !== null && record?.imageUrls?.length > 0 && (
                <ImagePreviewModal
                    selectedImage={record.imageUrls[previewIndex]}
                    images={record.imageUrls}
                    initialIndex={previewIndex}
                    onclose={() => setPreviewIndex(null)}
                />
            )}
        </div>
    );
};

export default ProductView;
